class LazyVideo extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.video = window.BAO.Utils.getElement('video', { context: this })

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })
    // We need to observe the video element as the host element has display: contents on it. IntersectionObserver won't work on an element that is set to display: contents
    this.observer.observe(this.video)

    if (this.video.dataset.playOnce === 'true') {
      this.video.addEventListener('ended', this.onVideoEnded.bind(this), {
        once: true,
      })
    }

    if (this.dataset.role && this.dataset.allowUnmute === 'true') {
      this.roleMediaQuery = window.matchMedia(
        `(min-width: ${window.BAO.MediaBreakpoints.breakpoints.md + 1}px)`,
      )
      this.listeners.add(
        this.roleMediaQuery,
        'change',
        this.onRoleVisibilityChange.bind(this),
      )
    }
  }

  onRoleVisibilityChange (event) {
    const isVisible =
      this.dataset.role === 'desktop' ? event.matches : !event.matches

    // Videos keep playing in the background, so mute them to avoid audio leaking when resizing screen
    if (!isVisible) this.video.muted = true
  }

  onVideoEnded () {
    const wrapper = this.closest('[data-lazy-video-external-el="wrapper"]')
    if (wrapper) wrapper.classList.add(`util-VideoEnded-${this.dataset.role}`)
  }

  get Play () {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <path fill="currentColor" d="M16.933 11.2a1 1 0 0 1 0 1.6l-9.333 7A1 1 0 0 1 6 19V5a1 1 0 0 1 1.6-.8z"/>
      </svg>
    `
  }

  get Pause () {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <rect width="5" height="16" x="5" y="4" fill="currentColor" rx="1"/>
        <rect width="5" height="16" x="14" y="4" fill="currentColor" rx="1"/>
      </svg>
    `
  }

  get VolumeOn () {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 12">
        <path fill="currentColor" d="M13.867 1.114a.88.88 0 0 0-1.245 0 .81.81 0 0 0 0 1.2c1.067 1.029 1.6 2.315 1.6 3.686s-.533 2.657-1.6 3.686a.81.81 0 0 0 0 1.2.98.98 0 0 0 .622.257.82.82 0 0 0 .623-.257C15.2 9.6 16 7.886 16 6s-.8-3.6-2.133-4.886"/>
        <path fill="currentColor" d="M10.028 2.813a.76.76 0 0 0 0 1.128c.653.63.885 1.25.885 2.059 0 .802-.379 1.643-.885 2.132a.76.76 0 0 0 0 1.128c.129.124.565.224.905.134.1-.027.192-.07.265-.134C12.243 8.342 12.56 6.994 12.56 6q0-.219-.01-.437c-.047-.85-.284-1.719-1.353-2.75a.83.83 0 0 0-1.17 0M3.365 3.436H.635c-.381 0-.635.257-.635.644v3.868c0 .386.254.644.635.644h2.73l3.62 3.307c.444.258 1.015 0 1.015-.516V.645A.634.634 0 0 0 6.984.129z"/>
      </svg>
    `
  }

  get VolumeOff () {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 12">
        <path fill="currentColor" d="m11.022 9 1.867-1.8L14.755 9 16 7.8 14.133 6 16 4.2 14.755 3l-1.867 1.8L11.023 3 9.777 4.2 11.644 6 9.777 7.8zM3.365 3.436H.635c-.381 0-.635.257-.635.644v3.868c0 .386.254.644.635.644h2.73l3.62 3.307c.444.258 1.015 0 1.015-.516V.645A.634.634 0 0 0 6.984.129z"/>
      </svg>
    `
  }

  addPlayPauseButton () {
    if (this.button) return

    const role = this.dataset.role // 'desktop' or 'mobile'
    const wrapper = this.closest('[data-lazy-video-external-el="wrapper"]')
    const target = wrapper || this

    this.button = document.createElement('button')
    this.button.className = `btn-PlayPause${role ? ` btn-PlayPause-${role}` : ''}`
    this.button.setAttribute('aria-label', 'Play/Pause decorative video')

    target.appendChild(this.button)

    this.video.addEventListener('play', () => {
      this.button.innerHTML = this.Pause
    })
    this.video.addEventListener('pause', () => {
      this.button.innerHTML = this.Play
    })

    this.button.addEventListener('click', () => this.togglePlayPause())

    this.button.innerHTML = this.video.paused ? this.Play : this.Pause
  }

  async togglePlayPause () {
    if (this.video.paused) {
      try {
        await this.video.play()
      } catch (error) {
        console.warn('Video play failed:', error)
      }
    } else {
      this.video.pause()
    }
  }

  addMuteButton () {
    if (this.muteButton) return

    const role = this.dataset.role // 'desktop' or 'mobile'
    const wrapper = this.closest('[data-lazy-video-external-el="wrapper"]')
    const target = wrapper || this

    this.muteButton = document.createElement('button')
    this.muteButton.className = `btn-Mute${role ? ` btn-Mute-${role}` : ''}`
    this.muteButton.setAttribute('aria-label', 'Mute/Unmute video')

    target.appendChild(this.muteButton)

    this.video.addEventListener('volumechange', () => {
      this.muteButton.innerHTML = this.video.muted
        ? this.VolumeOn
        : this.VolumeOff
    })

    this.muteButton.addEventListener('click', () => this.toggleMute())

    this.muteButton.innerHTML = this.video.muted
      ? this.VolumeOn
      : this.VolumeOff
  }

  toggleMute () {
    this.video.muted = !this.video.muted
  }

  onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion) {
      this.video.removeAttribute('autoplay')
      this.video.setAttribute('controls', '')
      this.video.pause()
    }

    if (this.video.dataset?.poster) {
      this.video.setAttribute('poster', this.video.dataset.poster)
    }

    for (const source in this.video.children) {
      const videoSource = this.video.children[source]

      if (
        typeof videoSource.tagName === 'string' &&
        videoSource.tagName === 'SOURCE'
      ) {
        if (videoSource.dataset.src) {
          videoSource.src = videoSource.dataset.src
        }
      }
    }

    this.video.load()
    observer.unobserve(this.video)

    this.setAttribute('loaded', '')

    if (this.video.dataset.playOnce !== 'true') {
      this.addPlayPauseButton()

      if (this.dataset.allowUnmute === 'true') {
        this.addMuteButton()
      }
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}
